<template>
    <div id="c-bot-chat-list">
        <supsis-table
            :columns="columns"
            :rows="rows"
            :bottom-pagination="true"
            :pagination-cursor="paginationCursor"
            @cursor-changed="(cursor) => (paginationCursor = cursor)"
        />
    </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";
import { library } from "@fortawesome/fontawesome-svg-core";
import { faCog, faComment, faCommentAlt, faCommentDots, faEye } from "@fortawesome/free-solid-svg-icons";
import PassedTimeCounter from "./PassedTimeCounter";
import PassedTimeCounterTimeFormat from "./PassedTimeCounterTimeFormat";
import CommentDots from "./svg/CommentDots";
import Info from "./svg/Info";
import Cog from "./svg/Cog";
import * as _ from "lodash";
import { log2 } from "../utils/log2";
import Information from "./svg/Information";
import Actions from "./svg/Actions";
import WatchIcon from "./svg/WatchIcon";
import Pagination from "./Pagination";
import { sendSocketMessage, socketMessageStream } from "../utils/socketStream";
import { filter, map } from "rxjs/operators";
import moment from "moment";
import { getPersistenceData } from "../utils/persistenceStorage";
import SupsisTable from "./SupsisTable.vue";
import TelegramIcon from "../views/Pages/Chatbot/pages/integrations/svg/TelegramIcon";
import WhatsappIcon from "../views/Pages/Chatbot/pages/integrations/svg/WhatsappIcon.vue";
import MessengerIcon from "../views/Pages/Chatbot/pages/integrations/svg/MessengerIcon.vue";
import InstagramIcon from "../views/Pages/Chatbot/pages/integrations/svg/InstagramIcon.vue";

library.add(faCommentDots, faCog, faEye, faComment, faCommentAlt);

const log = log2("BotChatList");
export default {
    name: "BotChatList",
    components: {
        SupsisTable,
        Actions,
        Information,
        PassedTimeCounter,
        PassedTimeCounterTimeFormat,
        CommentDots,
        Info,
        Cog,
        Pagination,
        WatchIcon,
        TelegramIcon,
        WhatsappIcon,
        MessengerIcon,
        InstagramIcon,
    },
    props: {},
    computed: {
        ...mapState({
            user: (state) => state.auth.user,
            site: (state) => state.site.site,
            isRoot: (state) => state.auth.user.role.isRoot,
            observing: (state) => state.chat.observing,
            totalChatCount: (state) => state.chat.stats.chatbot,
            settings: (state) => state.setting.settings,
            socketConnected: (state) => state.socketStatus.socket_connected,
            rows: (state) => _.sortBy(state.chat.latestChatbotChats, (x) => moment(x.createdDate).toDate().getTime()),
        }),
        ...mapGetters({
            findConversationByVisitorId: "chat/findConversationByVisitorId",
            isDarkMode: "user/isDarkMode",
        }),
        columns() {
            return [
                {
                    title: "",
                    field: "platformIcon",
                    key: "platformIcon",
                    width: 50,
                    renderBodyCell: ({ row }) => {
                        return this.renderChatIcon(row);
                    },
                },
                {
                    title: this.$t("message.fullname"),
                    field: "fullname",
                    key: "fullname",
                    width: 140,
                },
                {
                    title: this.$t("message.email"),
                    field: "email",
                    key: "email",
                    width: 140,
                },
                {
                    title: this.$t("message.date"),
                    field: "createdDate",
                    key: "createdDate",
                    width: 140,
                    renderBodyCell: ({ row }) => {
                        return this.$options.filters.dateShorted(row.createdDate);
                    },
                },
                {
                    title: this.$t("message.chatDuration"),
                    field: "chatDurations",
                    key: "chatDurations",
                    width: 140,
                    renderBodyCell: ({ row }) => {
                        return (
                            row.status === "chatbot" && <PassedTimeCounter date={row.createdDate} suffix=" saniye" />
                        );
                    },
                },

                {
                    title: this.$t("message.transactions"),
                    field: "actions",
                    key: "actions",
                    width: 140,
                    align: "right",
                    renderBodyCell: ({ row }) => {
                        return (
                            <div class="d-flex justify-content-end" style="margin-right: -10px !important">
                                <a-popover placement="bottom" title="" trigger="click">
                                    <template slot="content">
                                        <div class="content">
                                            {(row.users || []).filter((w) => w === this.user._id).length === 0 &&
                                                !this.observing.includes(row._id) && (
                                                    <div class="d-flex flex-row">
                                                        <div class="justify-content-start">
                                                            <watch-icon />
                                                        </div>
                                                        <div
                                                            class="justify-content-end pl-3 popoverFont cursorPointer"
                                                            on-click={() => this.observe(row)}
                                                        >
                                                            {this.$t("message.watch")}
                                                        </div>
                                                    </div>
                                                )}
                                        </div>
                                    </template>
                                    <md-button class="custom-button" data-v-step="15">
                                        <Actions></Actions>
                                    </md-button>
                                </a-popover>

                                <a-popover title="" trigger="click">
                                    <template slot="content">
                                        <div class="px-2">
                                            <b-row class="mb-3">
                                                <b-col md="6" style="color: #9ba2b6; font-weight: bold">
                                                    {this.$t("message.country")}:
                                                </b-col>
                                                <b-col md="6">
                                                    {row.geoLocation ? row.geoLocation.country : "N/A"}
                                                </b-col>
                                            </b-row>
                                            {this.$_.get(row, "geoLocation.city", false) && (
                                                <b-row class="mb-3">
                                                    <b-col md="6" style="color: #9ba2b6; font-weight: bold">
                                                        {this.$t("message.city")}:
                                                    </b-col>
                                                    <b-col md="6">{row.geoLocation.city}</b-col>
                                                </b-row>
                                            )}
                                            <b-row class="mb-3">
                                                <b-col md="6" style="color: #9ba2b6; font-weight: bold">
                                                    {this.$t("message.browser")}:
                                                </b-col>
                                                <b-col md="6">
                                                    {row.visitor.browser
                                                        ? row.visitor.browser
                                                        : this.getUserAgentInfo(row.userAgent, "browser")}
                                                </b-col>
                                            </b-row>
                                            <b-row class="mb-3">
                                                <b-col md="6" style="color: #9ba2b6; font-weight: bold">
                                                    {this.$t("message.os")}:
                                                </b-col>
                                                <b-col md="6">
                                                    {row.visitor.os
                                                        ? row.visitor.os
                                                        : this.getUserAgentInfo(row.userAgent, "os")}
                                                </b-col>
                                            </b-row>
                                            <b-row class="mb-3">
                                                <b-col md="6" style="color: #9ba2b6; font-weight: bold">
                                                    {this.$t("message.visitStart")}:
                                                </b-col>
                                                <b-col md="6">{this.$options.filters.date(row.createdDate)}</b-col>
                                            </b-row>
                                        </div>
                                    </template>
                                    <md-button class="custom-button" data-v-step="14">
                                        <Information></Information>
                                    </md-button>
                                </a-popover>

                                {(row.users || []).filter((w) => w === this.user._id).length === 0 &&
                                    !this.observing.includes(row._id) && (
                                        <md-button
                                            v-tippy={{
                                                followCursor: true,
                                                placement: "bottom-end",
                                                theme: this.isDarkMode ? "light-border" : "google",
                                            }}
                                            content={this.$t("message.watch")}
                                            class="custom-button"
                                            data-v-step="13"
                                            on-click={() => this.observe(row)}
                                        >
                                            <a-icon style="font-size: 14px; color: #49c550" theme="filled" type="eye" />
                                        </md-button>
                                    )}
                            </div>
                        );
                    },
                },
            ];
        },
    },
    data() {
        const lsKey = "chatbot-chatlist-page-size";
        return {
            pageSizes: ["10", "25", "50", "100"],
            activeModalActions: null,
            activeModalInfo: null,
            currentSort: {
                columnKey: "createdDate",
                order: "ascend",
            },
            loading: false,
            dataFetcherSubscriber: null,
            timerId: null,
            lastDataFetchTS: Date.now(),
            paginationCursor: {
                page: 1,
                perPage: !isNaN(getPersistenceData(lsKey) || "undefined") ? parseInt(getPersistenceData(lsKey)) : 10,
                total: 0,
            },
        };
    },
    mounted() {
        log("mounted");
        this.dataFetcherSubscriber = socketMessageStream
            .pipe(
                filter((x) => x.cmd === "live_chatbot_conversations_response"),
                map((x) => x.data)
            )
            .subscribe((data) => {
                const { conversations, total } = data;
                this.loading = false;
                this.paginationCursor.total = total;
                this.setLatestChatBotChats(conversations);
            });
        this.fetchPagedData();
        this.timerId = setInterval(this.fetchPagedData, 2000);
    },
    beforeDestroy() {
        if (this.dataFetcherSubscriber) this.dataFetcherSubscriber.unsubscribe();
        if (this.timerId) clearInterval(this.timerId);
    },
    updated() {},
    methods: {
        ...mapActions({
            joinConversation: "chat/joinConversation",
            getSettings: "setting/getSettings",
            setLatestChatBotChats: "chat/setLatestChatBotChats",
        }),
        renderChatIcon(row) {
            const whatsappIcon = !!row.whatsappNumber && (
                <md-icon
                    style={{ color: "green", fontSize: "18px" }}
                    v-tippy={{
                        followCursor: true,
                        placement: "bottom-end",
                        theme: this.isDarkMode ? "light-border" : "google",
                    }}
                    content="'Whatsapp Ziyaretçisi'"
                >
                    <WhatsappIcon color="green" />
                </md-icon>
            );
            const instagramIcon = !!row.instagramUserId && (
                <InstagramIcon
                    special-background-mode
                    style="width: 24px; height: 24px"
                    type="instagram"
                    v-tippy={{
                        followCursor: true,
                        placement: "bottom-end",
                        theme: this.isDarkMode ? "light-border" : "google",
                    }}
                    content={"Instagram Ziyaretçisi"}
                />
            );

            const messengerIcon = !!row.messengerUserId && (
                <MessengerIcon
                    style="width: 24px; height: 24px"
                    v-tippy={{
                        followCursor: true,
                        placement: "bottom-end",
                        theme: this.isDarkMode ? "light-border" : "google",
                    }}
                    content={"Messenger Ziyaretçisi"}
                />
            );

            const telegramIcon = !!row.telegramUserId && (
                <TelegramIcon
                    style="width: 24px; height: 24px"
                    v-tippy={{
                        followCursor: true,
                        placement: "bottom-end",
                        theme: this.isDarkMode ? "light-border" : "google",
                    }}
                    content={"Telegram Bot Ziyaretçisi"}
                />
            );
            const webVisitor = (
                <md-icon
                    style={{ color: "#14b9ec", fontSize: "18px" }}
                    v-tippy={{
                        followCursor: true,
                        placement: "bottom-end",
                        theme: this.isDarkMode ? "light-border" : "google",
                    }}
                    content={"Web Ziyaretçisi"}
                >
                    ads_click
                </md-icon>
            );

            return whatsappIcon || instagramIcon || messengerIcon || telegramIcon || webVisitor;
        },
        fetchPagedData: _.debounce(function () {
            log("fetchPagedData called");
            const tookTime = Date.now() - this.lastDataFetchTS;
            const isNotValid = this.loading && !this.socketConnected && tookTime <= 5000;
            if (isNotValid) {
                log.warn("[ChatList] fetchPagedData: socket is not connected, skip fetching data", {
                    tookTime,
                    loading: this.loading,
                    socketConnected: this.socketConnected,
                });
                return;
            }
            this.loading = true;
            this.lastDataFetchTS = Date.now();
            sendSocketMessage("user get-live-chatbot-conversations", {
                page: this.paginationCursor.page - 1,
                perPage: this.paginationCursor.perPage,
                sortCol: this.currentSort.columnKey,
                sortDirection: this.currentSort.order === "ascend" ? "ASC" : "DESC",
                siteId: this.site._id,
            });
        }, 1000),
        getUserAgentInfo(userAgent, info) {
            const parser = require("ua-parser-js");
            const ua = parser(userAgent || "-");
            if (info === "os") {
                return `${ua.os.name} ${ua.os.version}`;
            } else if (info === "browser") {
                return `${ua.browser.name} ${ua.browser.major}`;
            }
        },
        observe(conversation) {
            this.joinConversation({ conversation, observe: true });
        },
    },
    watch: {
        paginationCursor: {
            deep: true,
            handler(newVal, oldVal) {
                newVal = { page: newVal.page, perPage: newVal.perPage };
                oldVal = { page: oldVal.page, perPage: oldVal.perPage };
                if (!_.isEqual(newVal, oldVal)) {
                    this.fetchPagedData();
                }
            },
        },
        totalChatCount(newValue, oldValue) {
            if (newValue) {
                const total = newValue;
                const maxPage = Math.ceil(total / this.paginationCursor.perPage);
                if (this.paginationCursor.page > maxPage) {
                    this.paginationCursor.page = 0;
                } else {
                    if (this.rows.length < this.paginationCursor.perPage) {
                        this.fetchPagedData();
                    }
                }
                if (total < this.paginationCursor.perPage) {
                    this.fetchPagedData();
                }
            }
        },
    },
};
</script>

<style lang="scss">
#c-bot-chat-list {
    padding: 10px;
    .supsis-table {
        tr {
            height: max-content !important;
        }

        td {
            padding: 1px 5px !important;
        }

        .custom-button {
            max-width: 18px;
            min-width: 18px;
            max-height: 18px;
            border-radius: 50%;
            overflow: hidden;
            display: flex;
            justify-content: center;
            align-items: center;

            svg {
                width: 14px;
                height: 14px;
            }
        }
    }

    .empty {
        position: relative !important;
        margin-top: 0px !important;
        margin-bottom: 5px;
    }

    .ellipsis {
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
        max-width: 200px;
    }

    .cursorPointer {
        cursor: pointer;
    }

    .cursorPointer:hover {
        font-weight: 600;
    }

    .popoverFont {
        font-family: Poppins;
        font-size: 14px;
        color: #181c32;
        font-weight: 300;
    }
}
</style>

<style>
.ant-popover-content .ant-popover-inner {
    box-shadow: none;
}
</style>
